import { Permission, useUserPermissionGrantsQuery, WarehouseExecutionSystem } from '../../../graphql'
import { useDcAdminContext } from '../contexts/DcAdminContext'

/**
 * Whether this user may create a pick order at `storeId`.
 *
 * Two independent gates, because they answer different questions and
 * inventory-api enforces both:
 *
 * - **Permission** — CAN_CREATE_PICK_ORDER_BY_STORE_ID, scoped to the fulfilling
 *   location: *may this user pick here*.
 * - **Facility kind** — `InventoryLocation.warehouse.warehouseExecutionSystem`: *is picking
 *   by hand even applicable here*. A DC running the Numina warehouse execution system has its
 *   picking done by the conveyor, and `CreatePickOrder` rejects one outright. A
 *   permission cannot answer this — it is a fact about the site, true regardless
 *   of who is asking. It rides along on the DC list the header already loads.
 *
 * Uses `userPermissionGrants` (one row per permission, object ids grouped) the
 * way `useCanCreateEcommerceAdjustment` does, rather than `usePermissions` /
 * `checkPermissions`: those read the flat `userAuthorizations.permissions` list,
 * which returns a row per (permission, storeId) pair — thousands of rows for
 * admin accounts — and `checkPermissions` ignores `objectId` entirely, so it
 * cannot answer a store-scoped question at all.
 */
export default function useCanCreatePickOrder( storeId?: string | null ): boolean {
	const { dcs, loading: dcsLoading } = useDcAdminContext()

	const { data, loading } = useUserPermissionGrantsQuery( {
		variables: {
			permissions: [ Permission.CanCreatePickOrderByStoreId ],
			objectIds: storeId ? [ storeId ] : [],
		},
		skip: !storeId,
	} )

	if ( !storeId || loading || dcsLoading ) {
		return false
	}

	// Absent an answer, assume the DC runs the conveyor: offering hand-picking
	// where it does not apply produces a rejected create, which is worse than a
	// missing action.
	if ( dcs.find( ( dc ) => dc.id === storeId )?.warehouseExecutionSystem !== WarehouseExecutionSystem.Manual ) {
		return false
	}

	return !!data?.userPermissionGrants.some(
		( grant ) => grant.permission === Permission.CanCreatePickOrderByStoreId &&
			grant.objectIds.some( ( objectId ) => String( objectId ) === storeId ),
	)
}
